import { useTranslation } from "react-i18next"

import { Obj, load } from "scrivito"
import { CONFIRMATION_OVERLAY } from "shared/context/overlay/types"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ProductPositionInstallationReference } from "shared/types/__generated__/graphql"

interface UseDeleteInstallationReferenceArgs {
  references: ProductPositionInstallationReference[] | null | undefined
  onInstallationReferencesChange?: (
    refs: ProductPositionInstallationReference[] | null
  ) => void
}

export function useDeleteInstallationReference({
  references,
  onInstallationReferencesChange
}: UseDeleteInstallationReferenceArgs) {
  const { t } = useTranslation()
  const { openOverlay: openConfirmationOverlay } =
    useOverlay(CONFIRMATION_OVERLAY)

  return useEvent(
    (
      id: string,
      onDelete: (refs: ProductPositionInstallationReference[] | null) => void
    ) => {
      const reference = references?.find(r => r.id === id)

      const openOverlay = (title?: string) => {
        openConfirmationOverlay({
          id: "confirm-delete-installation-reference",
          headline: t("InstallationReference.DeleteConfirmation.Title"),
          message: title
            ? t("InstallationReference.DeleteConfirmation.Message", { title })
            : t("InstallationReference.DeleteConfirmation.MessageFallback"),
          isDanger: true,
          confirmButtonText: t("General.Delete"),
          onConfirm: () => {
            const filtered = references?.filter(r => r.id !== id) ?? []
            onInstallationReferencesChange?.(filtered)
            onDelete(filtered)
          }
        })
      }

      if (!reference) {
        openOverlay()
        return
      }

      load(() =>
        Obj.onSite(getCurrentSiteID())
          .where("_objClass", "equals", "InstallationReference")
          .and("referenceID", "equals", reference.permalink)
          .first()
      ).then(obj => {
        const title = obj?.get("title") as string | null | undefined
        openOverlay(title || undefined)
      })
    }
  )
}
